import React from 'react';

/**
 * Tiny, safe markdown-to-React renderer for assistant replies.
 *
 * Why not react-markdown? The project doesn't have it installed, and
 * adding an npm dependency would change CI/CD behavior. The assistant's
 * output is a controlled subset of GFM (headings, lists, bold, italic,
 * inline code, links) so a handwritten renderer is sufficient and safe
 * — every value is inserted via React (text nodes), not dangerouslySetInnerHTML.
 */

// Render a single line's inline markup (bold, italic, code, links).
function renderInline(text, keyPrefix = 'i') {
  // Order matters: code first, then links, then bold/italic.
  const tokens = [];
  let rest = text;
  let idx = 0;

  const pushText = (s) => {
    if (s) tokens.push(<React.Fragment key={`${keyPrefix}-t-${idx++}`}>{s}</React.Fragment>);
  };

  while (rest.length) {
    const codeMatch = rest.match(/^`([^`]+)`/);
    if (codeMatch) {
      tokens.push(
        <code
          key={`${keyPrefix}-c-${idx++}`}
          style={{
            background: 'rgba(0,0,0,0.06)',
            padding: '1px 4px',
            borderRadius: 3,
            fontSize: '0.9em',
          }}
        >
          {codeMatch[1]}
        </code>
      );
      rest = rest.slice(codeMatch[0].length);
      continue;
    }
    const linkMatch = rest.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      const href = linkMatch[2];
      const safe = /^(https?:|\/|mailto:)/.test(href) ? href : '#';
      tokens.push(
        <a key={`${keyPrefix}-l-${idx++}`} href={safe} target={safe.startsWith('/') ? '_self' : '_blank'} rel="noreferrer noopener">
          {linkMatch[1]}
        </a>
      );
      rest = rest.slice(linkMatch[0].length);
      continue;
    }
    const boldMatch = rest.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      tokens.push(<strong key={`${keyPrefix}-b-${idx++}`}>{boldMatch[1]}</strong>);
      rest = rest.slice(boldMatch[0].length);
      continue;
    }
    const italicMatch = rest.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      tokens.push(<em key={`${keyPrefix}-e-${idx++}`}>{italicMatch[1]}</em>);
      rest = rest.slice(italicMatch[0].length);
      continue;
    }
    // Consume until the next special marker
    const next = rest.search(/[`*\[]/);
    if (next === -1) {
      pushText(rest);
      break;
    }
    if (next === 0) {
      // Unmatched marker — emit literally and advance
      pushText(rest[0]);
      rest = rest.slice(1);
    } else {
      pushText(rest.slice(0, next));
      rest = rest.slice(next);
    }
  }

  return tokens;
}

export function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const out = [];
  let listBuf = null; // { ordered: bool, items: [] }
  let paraBuf = [];
  let key = 0;

  const flushPara = () => {
    if (!paraBuf.length) return;
    out.push(
      <p key={`p-${key++}`} style={{ margin: '0 0 6px 0', lineHeight: 1.45 }}>
        {renderInline(paraBuf.join(' '), `p-${key}`)}
      </p>
    );
    paraBuf = [];
  };

  const flushList = () => {
    if (!listBuf) return;
    const Tag = listBuf.ordered ? 'ol' : 'ul';
    out.push(
      <Tag
        key={`list-${key++}`}
        style={{ margin: '2px 0 8px 0', paddingLeft: 22, lineHeight: 1.45 }}
      >
        {listBuf.items.map((item, i) => (
          <li key={i}>{renderInline(item, `li-${key}-${i}`)}</li>
        ))}
      </Tag>
    );
    listBuf = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushPara();
      flushList();
      const level = heading[1].length;
      const Hx = `h${Math.min(level + 2, 6)}`;
      out.push(
        React.createElement(
          Hx,
          { key: `h-${key++}`, style: { margin: '10px 0 4px 0', fontSize: level === 1 ? '1.05rem' : '0.98rem' } },
          renderInline(heading[2], `h-${key}`)
        )
      );
      continue;
    }
    const ordered = line.match(/^\s*(\d+)\.\s+(.*)$/);
    const unordered = line.match(/^\s*[-*]\s+(.*)$/);
    if (ordered || unordered) {
      flushPara();
      const text = ordered ? ordered[2] : unordered[1];
      const isOrdered = Boolean(ordered);
      if (!listBuf || listBuf.ordered !== isOrdered) {
        flushList();
        listBuf = { ordered: isOrdered, items: [] };
      }
      listBuf.items.push(text);
      continue;
    }
    flushList();
    paraBuf.push(line.trim());
  }
  flushPara();
  flushList();
  return out;
}
